"use client";

import { useLayoutEffect, useMemo, useRef, useState, type ReactNode } from "react";
import {
  Bold,
  Download,
  Heading2,
  Info,
  Italic,
  Link as LinkIcon,
  List,
  ListOrdered,
} from "lucide-react";

function downloadFileNameFromLabel(label: string): string {
  const slug = label
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return `${slug || "prompt"}.md`;
}

function downloadTextFile(contents: string, filename: string) {
  const blob = new Blob([contents], { type: "text/markdown;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function insertAroundSelection(
  value: string,
  start: number,
  end: number,
  before: string,
  after: string,
  placeholder = "text",
): { next: string; selStart: number; selEnd: number } {
  const selected = value.slice(start, end) || placeholder;
  const next = value.slice(0, start) + before + selected + after + value.slice(end);
  const selStart = start + before.length;
  const selEnd = selStart + selected.length;
  return { next, selStart, selEnd };
}

function splitTableCells(line: string): string[] {
  const trimmed = line.trim().replace(/^\|/, "").replace(/\|$/, "");
  return trimmed.split("|").map((c) => c.trim());
}

function isTableRowLine(line: string): boolean {
  const t = line.trim();
  if (!t.includes("|")) return false;
  return /^\|?.+\|.+\|?$/.test(t) || /^\|.+\|$/.test(t);
}

function isTableSeparatorLine(line: string): boolean {
  const t = line.trim();
  if (!t.includes("-")) return false;
  const cells = splitTableCells(t);
  if (cells.length < 1) return false;
  return cells.every((c) => /^:?-{3,}:?$/.test(c.replace(/\s/g, "")));
}

/** Minimal markdown → React nodes for Preview (no extra dependency). */
export function renderSimpleMarkdown(src: string): ReactNode {
  const lines = src.replace(/\r\n/g, "\n").split("\n");
  const nodes: ReactNode[] = [];
  let i = 0;
  let key = 0;

  const inline = (text: string): ReactNode[] => {
    const out: ReactNode[] = [];
    const re = /(\*\*[^*]+\*\*|\*[^*]+\*|`[^`]+`|\[[^\]]+\]\([^)]+\))/g;
    let last = 0;
    let m: RegExpExecArray | null;
    while ((m = re.exec(text))) {
      if (m.index > last) out.push(text.slice(last, m.index));
      const tok = m[0];
      if (tok.startsWith("**")) out.push(<strong key={`b${key++}`}>{tok.slice(2, -2)}</strong>);
      else if (tok.startsWith("*")) out.push(<em key={`i${key++}`}>{tok.slice(1, -1)}</em>);
      else if (tok.startsWith("`")) out.push(<code key={`c${key++}`}>{tok.slice(1, -1)}</code>);
      else {
        const lm = tok.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
        if (lm) {
          out.push(
            <a key={`a${key++}`} href={lm[2]} target="_blank" rel="noreferrer">
              {lm[1]}
            </a>,
          );
        } else out.push(tok);
      }
      last = m.index + tok.length;
    }
    if (last < text.length) out.push(text.slice(last));
    return out;
  };

  while (i < lines.length) {
    const line = lines[i];

    if (isTableRowLine(line)) {
      const block: string[] = [];
      while (i < lines.length && isTableRowLine(lines[i])) {
        block.push(lines[i]);
        i += 1;
      }
      if (block.length >= 2) {
        const header = splitTableCells(block[0]);
        let bodyStart = 1;
        if (isTableSeparatorLine(block[1])) bodyStart = 2;
        const body = block.slice(bodyStart).map(splitTableCells);
        if (body.length > 0 || bodyStart === 2) {
          nodes.push(
            <div key={key++} className="agent-md-table-wrap">
              <table>
                <thead>
                  <tr>
                    {header.map((cell, ci) => (
                      <th key={ci}>{inline(cell)}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {body.map((row, ri) => (
                    <tr key={ri}>
                      {header.map((_, ci) => (
                        <td key={ci}>{inline(row[ci] ?? "")}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>,
          );
          continue;
        }
      }
      for (const raw of block) nodes.push(<p key={key++}>{inline(raw)}</p>);
      continue;
    }

    if (/^##\s+/.test(line)) {
      nodes.push(<h3 key={key++}>{inline(line.replace(/^##\s+/, ""))}</h3>);
      i += 1;
      continue;
    }
    if (/^#\s+/.test(line)) {
      nodes.push(<h2 key={key++}>{inline(line.replace(/^#\s+/, ""))}</h2>);
      i += 1;
      continue;
    }
    if (/^[-*]\s+/.test(line)) {
      const items: ReactNode[] = [];
      while (i < lines.length && /^[-*]\s+/.test(lines[i])) {
        items.push(<li key={key++}>{inline(lines[i].replace(/^[-*]\s+/, ""))}</li>);
        i += 1;
      }
      nodes.push(<ul key={key++}>{items}</ul>);
      continue;
    }
    if (/^\d+\.\s+/.test(line)) {
      const items: ReactNode[] = [];
      while (i < lines.length && /^\d+\.\s+/.test(lines[i])) {
        items.push(<li key={key++}>{inline(lines[i].replace(/^\d+\.\s+/, ""))}</li>);
        i += 1;
      }
      nodes.push(<ol key={key++}>{items}</ol>);
      continue;
    }
    if (!line.trim()) {
      nodes.push(<div key={key++} className="agent-md-gap" />);
      i += 1;
      continue;
    }
    nodes.push(<p key={key++}>{inline(line)}</p>);
    i += 1;
  }
  return nodes;
}

type Props = {
  value: string;
  onChange: (next: string) => void;
  max: number;
  label?: string;
  emptyHint?: string;
  placeholder?: string;
  /** Extra class on the textarea / preview body (height). */
  areaClassName?: string;
  spellCheck?: boolean;
  /** Downloaded file name (defaults from label). */
  downloadFileName?: string;
};

export function AgentMarkdownEditor({
  value,
  onChange,
  max,
  label = "Prompt",
  emptyHint,
  placeholder = "Enter prompt text…",
  areaClassName,
  spellCheck,
  downloadFileName,
}: Props) {
  const [mode, setMode] = useState<"edit" | "preview">("edit");
  const areaRef = useRef<HTMLTextAreaElement | null>(null);
  const caretRef = useRef<{ start: number; end: number; scroll: number } | null>(null);
  const preview = useMemo(() => renderSimpleMarkdown(value), [value]);

  useLayoutEffect(() => {
    const pending = caretRef.current;
    const el = areaRef.current;
    if (!pending || !el || mode !== "edit") return;
    caretRef.current = null;
    el.focus();
    el.setSelectionRange(pending.start, pending.end);
    el.scrollTop = pending.scroll;
  }, [value, mode]);

  function applyWrap(before: string, after: string, placeholderText?: string) {
    const el = areaRef.current;
    if (!el) {
      onChange(`${before}${placeholderText || "text"}${after}${value}`);
      return;
    }
    const start = el.selectionStart ?? value.length;
    const end = el.selectionEnd ?? value.length;
    const { next, selStart, selEnd } = insertAroundSelection(
      value,
      start,
      end,
      before,
      after,
      placeholderText,
    );
    caretRef.current = { start: selStart, end: selEnd, scroll: el.scrollTop };
    onChange(next);
  }

  return (
    <div className="agent-conv-editor">
      <div className="agent-conv-editor-head">
        <div className="agent-conv-editor-labels">
          <b>{label}</b>
          <span className="agent-conv-md-badge">Markdown supported</span>
        </div>
        <div className="agent-conv-editor-head-actions">
          <div className="agent-conv-mode" role="group" aria-label="Editor mode">
            <button
              type="button"
              className={mode === "edit" ? "is-active" : undefined}
              onClick={() => setMode("edit")}
            >
              Edit
            </button>
            <button
              type="button"
              className={mode === "preview" ? "is-active" : undefined}
              onClick={() => setMode("preview")}
            >
              Preview
            </button>
          </div>
          <button
            type="button"
            className="agent-conv-download"
            aria-label="Download"
            title="Download"
            onClick={() =>
              downloadTextFile(value, downloadFileName || downloadFileNameFromLabel(label))
            }
          >
            <Download size={14} strokeWidth={2} aria-hidden />
          </button>
        </div>
      </div>

      {mode === "edit" && (
        <div className="agent-conv-toolbar" role="toolbar" aria-label="Markdown">
          <button type="button" title="Bold" onMouseDown={(e) => e.preventDefault()} onClick={() => applyWrap("**", "**")}>
            <Bold size={14} />
          </button>
          <button type="button" title="Italic" onMouseDown={(e) => e.preventDefault()} onClick={() => applyWrap("*", "*")}>
            <Italic size={14} />
          </button>
          <button type="button" title="Heading" onMouseDown={(e) => e.preventDefault()} onClick={() => applyWrap("## ", "", "Heading")}>
            <Heading2 size={14} />
          </button>
          <button type="button" title="Bullet list" onMouseDown={(e) => e.preventDefault()} onClick={() => applyWrap("- ", "", "Item")}>
            <List size={14} />
          </button>
          <button type="button" title="Numbered list" onMouseDown={(e) => e.preventDefault()} onClick={() => applyWrap("1. ", "", "Item")}>
            <ListOrdered size={14} />
          </button>
          <button type="button" title="Link" onMouseDown={(e) => e.preventDefault()} onClick={() => applyWrap("[", "](https://)", "label")}>
            <LinkIcon size={14} />
          </button>
        </div>
      )}

      {mode === "edit" ? (
        <textarea
          ref={areaRef}
          className={`agent-conv-textarea${areaClassName ? ` ${areaClassName}` : ""}`}
          value={value}
          spellCheck={spellCheck}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
        />
      ) : (
        <div className={`agent-conv-preview${areaClassName ? ` ${areaClassName}` : ""}`}>
          {value.trim() ? preview : <p className="agent-conv-preview-empty">Nothing to preview.</p>}
        </div>
      )}

      <div className="agent-conv-editor-foot">
        {emptyHint ? (
          <p className="agent-conv-empty-hint">
            <Info size={13} aria-hidden />
            {emptyHint}
          </p>
        ) : (
          <span />
        )}
        <span className={`agent-conv-count${value.length > max ? " is-over" : ""}`}>
          {value.length.toLocaleString()} / {max.toLocaleString()}
        </span>
      </div>
    </div>
  );
}
